import { Plus, User } from 'lucide-react'
import { Fragment, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { parseError } from '@/lib/errors'
import { type SalesLead, useCreateContact } from '@/models/salesLead'
import { ContactRow } from './ContactRow'

interface ContactsCardProps {
  lead: SalesLead
}

interface NewContactForm {
  name: string
  primaryEmail: string
  primaryPhone: string
}

const EMPTY_FORM: NewContactForm = { name: '', primaryEmail: '', primaryPhone: '' }

export function ContactsCard({ lead }: ContactsCardProps) {
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState<NewContactForm>(EMPTY_FORM)
  const createMutation = useCreateContact(lead.id)

  function handleAdd() {
    createMutation.mutate(form, {
      onSuccess: () => {
        setAdding(false)
        setForm(EMPTY_FORM)
      },
      onError: (err) => toast.error(parseError(err)),
    })
  }

  const contacts = lead.contacts

  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <User className="size-4" />
          Contacts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {contacts.length === 0 && !adding && (
          <p className="text-muted-foreground text-sm italic">No contacts yet.</p>
        )}

        {contacts.map((contact, index) => (
          <Fragment key={contact.id}>
            {index > 0 && <Separator />}
            <ContactRow leadId={lead.id} contact={contact} isOnly={contacts.length === 1} />
          </Fragment>
        ))}

        {adding ? (
          <div className="space-y-2 rounded-md border border-dashed border-neutral-300 p-3">
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Contact name"
                className="h-7 px-2 py-0 text-sm"
                autoFocus
                disabled={createMutation.isPending}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                value={form.primaryEmail}
                onChange={(e) => setForm((f) => ({ ...f, primaryEmail: e.target.value }))}
                placeholder="email@example.com"
                className="h-7 px-2 py-0 text-sm"
                disabled={createMutation.isPending}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Phone</Label>
              <Input
                type="tel"
                value={form.primaryPhone}
                onChange={(e) => setForm((f) => ({ ...f, primaryPhone: e.target.value }))}
                placeholder="+1 555 000 0000"
                className="h-7 px-2 py-0 text-sm"
                disabled={createMutation.isPending}
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                onClick={handleAdd}
                loading={createMutation.isPending ? 'Adding…' : undefined}
              >
                Add
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAdding(false)
                  setForm(EMPTY_FORM)
                }}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground h-6 w-full justify-start px-2 text-xs"
            onClick={() => setAdding(true)}
          >
            <Plus className="mr-1 size-3" />
            Add contact
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
